import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import ImageLightbox from '../../../../components/ui/ImageLightbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import { getMatch } from '@/api/TournamentManagement/matchApi';
import { MatchStatus, MatchType, MatchFormat, MatchRole, RefereeCertification, AgeCategory, resolveEnum } from '@/models/tournament';
import type { MatchView } from '@/models/tournament';
import { parseApiWallClock } from '@/utils/apiDate';

interface MatchDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  match: MatchView | null;
  /** Hide referee-rating UI when viewer is a referee (non-HR). */
  isReferee?: boolean;
  /** Referees only see their own work points on member cards. */
  currentUserId?: string;
}

const MatchDetailModal: React.FC<MatchDetailModalProps> = ({ isOpen, onClose, match: matchProp, isReferee, currentUserId }) => {
  const { t, i18n } = useTranslation('tournament');
  const [match, setMatch] = useState<MatchView | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !matchProp) {
      setMatch(null);
      return;
    }
    setMatch(matchProp);
    setLoadingDetails(true);
    getMatch(matchProp.id)
      .then((result) => {
        if (result.errors.length === 0 && result.payload) {
          setMatch(result.payload);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingDetails(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, matchProp?.id]);

  if (!isOpen || !match) return null;

  const rs = (status: MatchStatus | string): MatchStatus =>
    (resolveEnum(MatchStatus, status) as MatchStatus) ?? MatchStatus.Scheduled;

  // Kickoff is company wall-clock (stored as-is) — never converted through the viewer's timezone.
  const formatDateTime = (dateTimeString: string, timeUnknown = false): string => {
    const date = parseApiWallClock(dateTimeString) ?? new Date(dateTimeString);
    return date?.toLocaleDateString(i18n.language, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      // Omit the time when the kickoff time is unknown — the date is real but 00:00 is a placeholder.
      ...(timeUnknown ? {} : { hour: '2-digit', minute: '2-digit' }),
    });
  };

  const statusKeys: Record<MatchStatus, string> = {
    [MatchStatus.Scheduled]: 'Scheduled',
    [MatchStatus.InProgress]: 'In Progress',
    [MatchStatus.Completed]: 'Completed',
    [MatchStatus.Closed]: 'Closed',
    [MatchStatus.UnderRevision]: 'Under Revision',
    [MatchStatus.Cancelled]: 'Cancelled',
  };

  const typeKeys: Record<MatchType, string> = {
    [MatchType.Outdoor]: 'Outdoor',
    [MatchType.Indoor]: 'Indoor',
  };

  const formatKeys: Record<MatchFormat, string> = {
    [MatchFormat.FiveAside]: '5-a-side',
    [MatchFormat.SevenAside]: '7-a-side',
    [MatchFormat.NineAside]: '9-a-side',
    [MatchFormat.ElevenAside]: '11-a-side',
  };

  const roleKeys: Record<MatchRole, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const certificationKeys: Record<RefereeCertification, string> = {
    [RefereeCertification.NationalLevelA]: 'National Level A',
    [RefereeCertification.AFCElite]: 'AFC Elite',
    [RefereeCertification.FIFAInternational]: 'FIFA International',
  };

  // AgeCategory reaches the client as its enum identifier ("Senior", "U18"). It is user-facing text
  // and was rendered raw; it goes through t() like every other enum label in this module.
  const ageCategoryLabel = (value: AgeCategory | string): string => {
    const key = AgeCategory[resolveEnum(AgeCategory, value) ?? -1];
    return key ? t(key) : '—';
  };

  const statusConfig: Record<MatchStatus, { bg: string; text: string; icon: string }> = {
    [MatchStatus.Scheduled]: { bg: 'bg-blue-100', text: 'text-blue-800', icon: 'Clock' },
    [MatchStatus.InProgress]: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: 'Play' },
    [MatchStatus.Completed]: { bg: 'bg-green-100', text: 'text-green-800', icon: 'CheckCircle' },
    [MatchStatus.Closed]: { bg: 'bg-gray-100', text: 'text-gray-800', icon: 'Lock' },
    [MatchStatus.UnderRevision]: { bg: 'bg-orange-100', text: 'text-orange-800', icon: 'AlertTriangle' },
    [MatchStatus.Cancelled]: { bg: 'bg-red-100', text: 'text-red-800', icon: 'XCircle' },
  };

  const handleDownloadAttachment = (): void => {
    if (match?.attachmentUrl) {
      const link = document.createElement('a');
      link.href = match.attachmentUrl;
      link.download = 'match-report';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    }
  };

  const currentStatus = rs(match?.status);
  const sc = statusConfig[currentStatus] ?? statusConfig[MatchStatus.Scheduled];
  const isCompleted = currentStatus === MatchStatus.Completed || currentStatus === MatchStatus.Closed || currentStatus === MatchStatus.UnderRevision;
  const hasExtraTime = isCompleted && match?.extraTimeTeam1Score != null && match?.extraTimeTeam2Score != null;
  const hasPenalties = isCompleted && match?.penaltyTeam1Score != null && match?.penaltyTeam2Score != null;

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Icon
          key={star}
          name="Star"
          size={14}
          className={rating >= star ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
        />
      ))}
      <span className="text-xs text-muted-foreground ms-1">{rating.toFixed(1)}</span>
    </div>
  );

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      {/* The header X is kept instead of Dialog's built-in close: it sits inside the header row next
          to the status chip, and E2E specs address it by data-testid. */}
      <DialogContent size="xl" hideCloseButton data-testid="match-detail-modal">
        {/* Header */}
        <DialogHeader className="sticky top-0 bg-card py-4 z-10">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <DialogTitle className="flex items-center">
                <Icon name="Trophy" size={22} className="me-2 text-primary shrink-0" />
                {t('Match Details')}
              </DialogTitle>
              {match?.tournamentName && (
                <DialogDescription className="mt-0.5 ms-[30px] truncate">{match.tournamentName}</DialogDescription>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${sc.bg} ${sc.text}`} data-testid="match-detail-status">
                <Icon name={sc.icon} size={14} />
                {t(statusKeys[currentStatus] ?? 'Unknown')}
              </span>
              <button
                type="button"
                onClick={onClose}
                aria-label={t('Close') as string}
                className="p-2 hover:bg-muted rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary/20"
                data-testid="match-detail-close-x-btn"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          {/* Scoreboard Card */}
          <div className="bg-gradient-to-br from-primary/5 via-blue-50/50 to-primary/5 dark:from-primary/10 dark:via-card dark:to-primary/10 rounded-xl border border-primary/10 overflow-hidden">
            {/* Teams & Score */}
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-center gap-4 sm:gap-8">
                {/* Team 1 */}
                <div className="text-center flex-1 min-w-0">
                  <div className="w-14 h-14 bg-white dark:bg-muted rounded-full flex items-center justify-center mb-2 mx-auto shadow-sm border border-border">
                    <Icon name="Shield" size={28} className="text-primary" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-foreground truncate">{match?.team1Name}</h3>
                </div>

                {/* Score / VS */}
                <div className="shrink-0 text-center">
                  {isCompleted && match?.team1Score != null && match?.team2Score != null ? (
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="text-4xl sm:text-5xl font-bold text-foreground tabular-nums">{match.team1Score}</span>
                        <span className="text-lg text-muted-foreground/60 font-medium">-</span>
                        <span className="text-4xl sm:text-5xl font-bold text-foreground tabular-nums">{match.team2Score}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 font-medium uppercase tracking-wider">{t('Full Time')}</p>
                    </div>
                  ) : (
                    <span className="text-2xl font-bold text-muted-foreground/50">{t('VS')}</span>
                  )}
                </div>

                {/* Team 2 */}
                <div className="text-center flex-1 min-w-0">
                  <div className="w-14 h-14 bg-white dark:bg-muted rounded-full flex items-center justify-center mb-2 mx-auto shadow-sm border border-border">
                    <Icon name="Shield" size={28} className="text-blue-600" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-foreground truncate">{match?.team2Name}</h3>
                </div>
              </div>

              {/* Extra Time & Penalties */}
              {(hasExtraTime || hasPenalties) && (
                <div className="flex items-center justify-center gap-6 mt-3 pt-3 border-t border-primary/10">
                  {hasExtraTime && (
                    <div className="text-center">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">{t('Extra Time')}</p>
                      <p className="text-lg font-semibold text-foreground tabular-nums">
                        {match.extraTimeTeam1Score} - {match.extraTimeTeam2Score}
                      </p>
                    </div>
                  )}
                  {hasPenalties && (
                    <div className="text-center">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">{t('Penalties')}</p>
                      <p className="text-lg font-semibold text-foreground tabular-nums">
                        {match.penaltyTeam1Score} - {match.penaltyTeam2Score}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Winner */}
              {isCompleted && match?.winnerTeamName && (
                <div className="flex items-center justify-center gap-1.5 mt-3">
                  <Icon name="Trophy" size={14} className="text-amber-500" />
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-400">{t('Winner')}: {match.winnerTeamName}</span>
                </div>
              )}
            </div>

            {/* Date & Location Bar */}
            <div className="bg-white/60 dark:bg-muted/40 border-t border-primary/10 px-6 py-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Icon name="Calendar" size={14} className="shrink-0" />
                {formatDateTime(match?.scheduledDateTime ?? '', match?.scheduledTimeUnknown)}
                {match?.scheduledTimeUnknown && (
                  <span className="italic text-muted-foreground">· {t('Time TBD')}</span>
                )}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Icon name="MapPin" size={14} className="shrink-0" />
                {match?.location}
              </span>
            </div>
          </div>

          {/* Metadata Chips */}
          <div className="flex flex-wrap gap-2">
            {match?.type != null && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-muted text-foreground border border-border">
                <Icon name={resolveEnum(MatchType, match.type) === MatchType.Indoor ? 'Home' : 'Sun'} size={13} />
                {t(typeKeys[(resolveEnum(MatchType, match.type) as MatchType)] ?? 'Unknown')}
              </span>
            )}
            {match?.format != null && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-muted text-foreground border border-border">
                <Icon name="Users" size={13} />
                {t(formatKeys[(resolveEnum(MatchFormat, match.format) as MatchFormat)] ?? 'Unknown')}
              </span>
            )}
            {match?.requiredCertification != null && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
                <Icon name="Award" size={13} />
                {t(certificationKeys[(resolveEnum(RefereeCertification, match.requiredCertification) as RefereeCertification)] ?? 'Unknown')}
              </span>
            )}
            {match?.pitch && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-muted text-foreground border border-border">
                <Icon name="MapPin" size={13} />
                {match.pitch}
              </span>
            )}
            {match?.ageCategory != null && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-teal-50 text-teal-800 border border-teal-200 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-800">
                <Icon name="Users" size={13} />
                {ageCategoryLabel(match.ageCategory)}
              </span>
            )}
            {match?.division && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-800 border border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-800">
                <Icon name="Layers" size={13} />
                {match.division}
              </span>
            )}
          </div>

          {/* Reason recorded with the last HR status change (cancel / revision / reopen / restore) */}
          {match?.statusReason && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-4" data-testid="match-status-reason">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Info" size={16} className="text-amber-700 dark:text-amber-400" />
                <h4 className="text-sm font-medium text-amber-900 dark:text-amber-200">{t('Status change reason')}</h4>
              </div>
              <p className="text-sm text-amber-800 dark:text-amber-300 whitespace-pre-wrap">{match.statusReason}</p>
            </div>
          )}

          {/* Comments & Attachment — side by side when both exist */}
          {isCompleted && (match?.completionComments || match?.attachmentUrl) && (
            <div className={`grid gap-4 ${match?.completionComments && match?.attachmentUrl ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
              {match?.completionComments && (
                <div className="bg-muted/50 rounded-lg p-4 border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
                    <h4 className="text-sm font-medium text-foreground">{t('Comments')}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{match.completionComments}</p>
                </div>
              )}
              {match?.attachmentUrl && (
                <div className="bg-muted/50 rounded-lg p-4 border border-border">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon name="Paperclip" size={16} className="text-muted-foreground shrink-0" />
                      <h4 className="text-sm font-medium text-foreground truncate">{t('Match Attachment')}</h4>
                    </div>
                    <button
                      type="button"
                      onClick={handleDownloadAttachment}
                      aria-label={t('Download') as string}
                      title={t('Download') as string}
                      className="shrink-0 p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon name="Download" size={16} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setLightboxOpen(true)}
                    aria-label={t('Enlarge') as string}
                    className="group relative block w-full overflow-hidden rounded-md border border-border bg-muted/50 focus-visible:ring-2 focus-visible:ring-primary outline-none"
                    data-testid="match-detail-attachment-thumbnail"
                  >
                    <img
                      src={match.attachmentUrl}
                      alt={t('Match Attachment') as string}
                      loading="lazy"
                      className="w-full max-h-40 object-cover transition-transform group-hover:scale-[1.02]"
                      onError={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        el.style.display = 'none';
                      }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none">
                      <Icon name="ZoomIn" size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                  </button>
                  {match?.attachmentUploadedByName && (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      {t('Uploaded by')} {match.attachmentUploadedByName}
                      {match.attachmentUploadedOn && ' • ' + new Date(match.attachmentUploadedOn).toLocaleString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Referee Team */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Users" size={18} className="text-muted-foreground" />
              <h3 className="text-sm font-semibold text-foreground">
                {t('Referee Team Members')} {loadingDetails ? '' : `(${match?.refereeTeamMembers?.length ?? 0})`}
              </h3>
              {loadingDetails && <Icon name="Loader2" size={14} className="animate-spin text-muted-foreground" />}
            </div>
            <div className="space-y-2">
              {match?.refereeTeamMembers?.map((member) => {
                // Referees only see points for their own card; HR/Admin see all.
                const canSeeWorkPoints = !isReferee || member?.userId === currentUserId;
                return (
                <div key={member?.id} className="bg-muted/40 rounded-lg px-4 py-3 border border-border hover:bg-muted/60 transition-colors">
                  {/* The identity group and the rating/points group used to sit on one unbreakable
                      row: the right-hand group was `shrink-0` (a 5-star rating plus a
                      `whitespace-nowrap` pill, ~150px that refuses to give) while the left group was
                      `min-w-0`, so at 320px the left column absorbed the entire deficit and
                      collapsed to ZERO width — the role label measured 0×32 and painted its glyphs
                      over the rating. The row now WRAPS instead: the identity group carries a
                      12rem flex-basis, so when 12rem + the rating group no longer fit on one line
                      the rating group moves to its own line rather than crushing the name and role. */}
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                    {/* Left: Avatar + Name + Role */}
                    <div className="flex items-center gap-3 min-w-0 grow shrink basis-48">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                        <Icon name="User" size={18} className="text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{member?.userName}</p>
                        {/* `truncate` is not decoration here: without it this <p> is the only child of
                            a min-w-0 column with no ellipsis fallback, which is exactly how it
                            degraded to a zero-width box instead of failing visibly. */}
                        <p className="text-xs text-muted-foreground truncate">{t(roleKeys[(resolveEnum(MatchRole, member?.role) as MatchRole) ?? member?.role] ?? 'Unknown')}</p>
                      </div>
                    </div>

                    {/* Right: Rating + Points */}
                    <div className="flex items-center gap-3 shrink-0">
                      {!isReferee && isCompleted && member?.matchRating != null && member.matchRating > 0 && (
                        renderStars(member.matchRating)
                      )}
                      {canSeeWorkPoints && (member?.workPointOverride != null && member.workPoints === 0 ? (
                        <span className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                          {t('Override')}: {member.workPointOverride} {t('pts')}
                        </span>
                      ) : member?.workPoints > 0 ? (
                        <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                          {member.workPoints} {t('pts')}
                        </span>
                      ) : null)}
                    </div>
                  </div>

                  {/* Second row: Contact.
                      An e-mail address carries no natural break opportunity, so at 320px the
                      address ran past the dialog's own client width (measured 342 > 318) and was
                      clipped mid-string. Each contact item is now a shrinkable flex item
                      (`min-w-0 max-w-full`) and the address itself may break anywhere, so the whole
                      address stays readable on two lines instead of being silently cut. It is
                      wrapped rather than truncated on purpose: an address is an identifier, and an
                      ellipsis would hide WHICH referee you are looking at. */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 ms-[52px] text-xs text-muted-foreground">
                    {member?.phone && (
                      <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                        <Icon name="Phone" size={12} className="text-gray-400 shrink-0" />
                        <span className="font-mono min-w-0 break-all">{member.phone}</span>
                      </span>
                    )}
                    {member?.email && (
                      <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                        <Icon name="Mail" size={12} className="text-gray-400 shrink-0" />
                        <span className="min-w-0 break-all">{member.email}</span>
                      </span>
                    )}
                  </div>

                  {/* Detailed Work Points Breakdown — matches the table view. Hidden for other referees. */}
                  {canSeeWorkPoints && (
                  <div className="ms-[52px] mt-2" data-testid={`match-detail-breakdown-${member?.id}`}>
                    {/* Pre-completion override indicator */}
                    {!member?.workPointBreakdown && member?.workPointOverride != null && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2">
                        <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                          <Icon name="Edit3" size={12} className="inline me-1" />
                          {t('Override')}: {member.workPointOverride} {t('pts')} — {t('Applied on completion')}
                        </p>
                      </div>
                    )}
                    {/* Full breakdown after completion */}
                    {member?.workPointBreakdown && (() => {
                      try {
                        const bd = JSON.parse(member.workPointBreakdown);
                        const isOverridden = bd.isOverridden || bd.IsOverridden;
                        if (isOverridden) {
                          return (
                            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2">
                              <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">{t('Work Point Breakdown')}</p>
                              <div className="flex flex-wrap gap-1.5 text-xs">
                                <span className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded font-semibold">{member.workPoints} {t('pts')}</span>
                                <span className="text-gray-400">({t('calculated')}: {bd.calculatedPoints ?? bd.CalculatedPoints ?? 0})</span>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="bg-muted/50 rounded-lg p-2">
                            <p className="text-xs font-medium text-foreground mb-1">{t('Work Point Breakdown')}</p>
                            <div className="flex flex-wrap gap-1.5 text-xs">
                              <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{t('Base')}: {bd.baseRolePoints ?? bd.BaseRolePoints ?? 0}</span>
                              <span className="text-gray-400">+</span>
                              <span className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{t('Format')}: {bd.formatPoints ?? bd.FormatPoints ?? 0}</span>
                              <span className="text-gray-400">+</span>
                              <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{t('Type')}: {bd.typeBonus ?? bd.TypeBonus ?? 0}</span>
                              <span className="text-gray-400">+</span>
                              <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">{t('Time')}: {bd.timeBonus ?? bd.TimeBonus ?? 0}</span>
                              <span className="text-gray-400">+</span>
                              <span className="bg-pink-50 text-pink-700 px-1.5 py-0.5 rounded">{t('Performance')}: {bd.performanceBonus ?? bd.PerformanceBonus ?? 0}</span>
                              <span className="text-gray-400">=</span>
                              <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded font-semibold">{t('Total')}: {member.workPoints}</span>
                            </div>
                          </div>
                        );
                      } catch {
                        return null;
                      }
                    })()}
                  </div>
                  )}
                </div>
                );
              })}
              {!loadingDetails && (!match?.refereeTeamMembers || match.refereeTeamMembers.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">{t('No referees assigned')}</p>
              )}
            </div>
          </div>
        </DialogBody>

        {/* Footer */}
        <DialogFooter className="py-4">
          <Button onClick={onClose} data-testid="match-detail-close-btn">
            {t('Close')}
          </Button>
        </DialogFooter>
      </DialogContent>

      <ImageLightbox
        src={match?.attachmentUrl ?? null}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        alt={t('Match Attachment') as string}
      />
    </Dialog>
  );
};

export default MatchDetailModal;
